import apiService from '../api/apiService';
import { updateUserToken, setLanguage } from '../store/correcttrackSlice';
import AuthUtil from '../components/Utils/AuthUtil';
import jwtDecode from 'jwt-decode';
import i18n, { SUPPORTED_LOCALES } from '../i18n';

let store;

export const injectStoreForAuth = _store => {
    store = _store;
};

class AuthService {
    _baseUrl = 'api/Auth/';

    async login (username, password) {
        const result = await apiService.postData(this._baseUrl + '/login', {
            username,
            password,
        });
        if (result.status === 200 && result.data.success) {
            store.dispatch(updateUserToken(result.data.data));
            try {
                const claim = jwtDecode(result.data.data.token)?.locale;
                if (claim && SUPPORTED_LOCALES.includes(claim)) {
                    store.dispatch(setLanguage(claim));
                    i18n.changeLanguage(claim);
                }
            } catch (_) { /* token without locale claim is fine */ }
        }
        return result;
    }

    async logout () {
        const user = AuthUtil.getUsername(store.getState().correcttrack.userToken);
        const refreshToken = store.getState().correcttrack.refreshToken;

        const result = await apiService.postData(this._baseUrl + '/logout', {
            username: user,
            refreshtoken: refreshToken,
        });
        await result;
        if (result.status === 200) {
            return true;
        }
        return false;
    }

    /**
     * Start impersonating a user as a super admin. On success the stored session is replaced with
     * the impersonated one, so callers should send the user to a fresh page afterwards rather than
     * leaving components holding data loaded as the previous identity.
     * @param targetUserId {number}
     * @return {Promise<ServiceResponse<JwtResponseDto>>}
     */
    async impersonate (targetUserId) {
        const result = await apiService.postData(this._baseUrl + '/impersonate', {
            targetUserId,
        });

        if (result.status === 200 && result.data.success) {
            store.dispatch(updateUserToken(result.data.data));
        }

        return result.data;
    }

    /**
     * End an impersonation session and return to the super admin's own account.
     * @return {Promise<ServiceResponse<JwtResponseDto>>}
     */
    async stopImpersonating () {
        // The account this token belongs to is read from the token server-side; only the refresh
        // token needs to be sent.
        const refreshToken = store.getState().correcttrack.refreshToken;

        const result = await apiService.postData(this._baseUrl + '/stop-impersonating', {
            refreshToken,
        });

        if (result.status === 200 && result.data.success) {
            store.dispatch(updateUserToken(result.data.data));
        }

        return result.data;
    }

    /**
     * Request a password reset
     * @param username {string}
     * @return {Promise<ServiceResponse<boolean>>}
     */
    async requestResetPassword(username) {
        const result = await apiService.postData(`${this._baseUrl}/reset-password/${username}`, null);

        return result.data;
    }

    /**
     * Submit password reset
     * @param passwordRequest {PasswordResetSubmissionDTO}
     * @return {Promise<ServiceResponse<boolean>>}
     */
    async submitChangedPassword(passwordRequest) {
        const result = await apiService.postData(`${this._baseUrl}/reset-user-password`, passwordRequest);

        return result.data;
    }
}

const authService = new AuthService();
export default authService;
